import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildPnlSharePayload } from "../utils/pnlOgImage/buildPnlSharePayload";
import { generatePnlOgImage } from "../utils/pnlOgImage/generatePnlOgImage";
import { loadProfileImageForCanvas } from "../utils/pnlOgImage/loadProfileImageForCanvas";
import { notifyError, notifySuccess } from "../utils/function";

const SHARE_SITE_URL = "https://pnl.fortunefoes.com/";
const PNL_SHARE_BONUS_STORAGE_PREFIX = "pnl_share_bonus_claimed_";

function getShareBonusStorageKey(walletAddress) {
  if (!walletAddress || typeof walletAddress !== "string") return null;
  return `${PNL_SHARE_BONUS_STORAGE_PREFIX}${walletAddress.toLowerCase()}`;
}

function readShareBonusClaimedFromStorage(walletAddress) {
  const key = getShareBonusStorageKey(walletAddress);
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeShareBonusClaimedToStorage(walletAddress) {
  const key = getShareBonusStorageKey(walletAddress);
  if (!key) return;
  try {
    localStorage.setItem(key, "1");
  } catch {
    // ignore quota / private mode
  }
}

/** Backend may expose any of these once the 1000-pt share bonus was claimed. */
function isShareBonusClaimedOnUser(user) {
  if (!user || typeof user !== "object") return false;
  return Boolean(
    user.shareBonusClaimed ??
    user.pnlShareBonusClaimed ??
    user.isPnlShareBonusClaimed ??
    user.hasClaimedPnlShareBonus ??
    user.is_Pnl_Share_Bonus_Claimed
  );
}

function buildShareTweetText(payload) {
  const pnl = Number(payload.pnlEth) || 0;
  const roi = Number(payload.roiPct) || 0;
  const roiLabel = roi > 0 ? `+${Math.round(roi)}%` : `${Math.round(roi)}%`;
  return `My NFT PNL is ${pnl.toFixed(2)} ETH (${roiLabel} ROI) — Top ${payload.topPercent}% NFT Trader on Fortune Foes 🏆\n${SHARE_SITE_URL}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function copyImageToClipboard(blob) {
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    return false;
  }
  await navigator.clipboard.write([
    new ClipboardItem({ "image/png": blob }),
  ]);
  return true;
}

/**
 * Generate, download, and share the Your PNL OG card from live tab data.
 */
export function usePnlShareImage({
  liveUser,
  userRank,
  profileDisplayLabel,
  profileSrc,
  profileImageRef,
  isConnected,
  isDataLoading,
}) {
  const [activeAction, setActiveAction] = useState(null);
  const [profileImagePreloaded, setProfileImagePreloaded] = useState(null);
  const [shareBonusClaimed, setShareBonusClaimed] = useState(false);
  const actionRef = useRef(null);

  const walletAddress = liveUser?.walletAddress ?? null;

  const profileImageEl = profileImageRef?.current ?? null;

  useEffect(() => {
    if (!walletAddress) {
      setShareBonusClaimed(false);
      return;
    }
    setShareBonusClaimed(
      isShareBonusClaimedOnUser(liveUser) ||
      readShareBonusClaimedFromStorage(walletAddress)
    );
  }, [walletAddress, liveUser]);

  const markShareBonusClaimed = useCallback(() => {
    if (!walletAddress) return;
    setShareBonusClaimed(true);
    writeShareBonusClaimedToStorage(walletAddress);
  }, [walletAddress]);

  const payload = useMemo(
    () =>
      buildPnlSharePayload({
        liveUser,
        userRank,
        profileDisplayLabel,
        profileSrc,
        profileImageEl,
        profileImagePreloaded,
      }),
    [
      liveUser,
      userRank,
      profileDisplayLabel,
      profileSrc,
      profileImageEl,
      profileImagePreloaded,
    ]
  );

  const canShare = isConnected && !isDataLoading;

  useEffect(() => {
    if (!profileSrc) {
      setProfileImagePreloaded(null);
      return undefined;
    }

    let cancelled = false;

    const preload = () => {
      const domImg = profileImageRef?.current ?? null;
      loadProfileImageForCanvas(profileSrc, domImg).then((img) => {
        if (!cancelled && img) setProfileImagePreloaded(img);
      });
    };

    preload();

    const domImg = profileImageRef?.current;
    if (domImg && !domImg.complete) {
      domImg.addEventListener("load", preload, { once: true });
      return () => {
        cancelled = true;
        domImg.removeEventListener("load", preload);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [profileSrc, profileImageRef]);

  const generateBlob = useCallback(async () => {
    let avatar = profileImagePreloaded;
    if (!avatar?.naturalWidth && profileSrc) {
      avatar = await loadProfileImageForCanvas(
        profileSrc,
        profileImageRef?.current ?? null
      );
      if (avatar) setProfileImagePreloaded(avatar);
    }

    return generatePnlOgImage({
      ...payload,
      profileImagePreloaded: avatar,
    });
  }, [payload, profileImagePreloaded, profileSrc, profileImageRef]);

  const beginAction = useCallback((action) => {
    if (actionRef.current) return false;
    actionRef.current = action;
    setActiveAction(action);
    return true;
  }, []);

  const endAction = useCallback(() => {
    actionRef.current = null;
    setActiveAction(null);
  }, []);

  const downloadImage = useCallback(async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!canShare) {
      notifyError("Connect your wallet and wait for PNL data to load.");
      return;
    }
    if (!beginAction("download")) return;

    try {
      const blob = await generateBlob();
      const handle = (payload.username || "trader").replace(/^@+/, "");
      downloadBlob(blob, `fortune-foes-pnl-${handle}.png`);
    } catch (err) {
      console.error(err);
      notifyError("Could not generate share image. Please try again.");
    } finally {
      endAction();
    }
  }, [beginAction, canShare, endAction, generateBlob, payload.username]);

  const shareOnX = useCallback(async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!canShare) {
      notifyError("Connect your wallet and wait for PNL data to load.");
      return;
    }
    if (!beginAction("share")) return;

    const text = buildShareTweetText(payload);
    const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    const xWindow = window.open(intentUrl, "_blank", "noopener,noreferrer");

    try {
      const blob = await generateBlob();
      const file = new File([blob], "fortune-foes-pnl.png", { type: "image/png" });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            text,
            files: [file],
          });
          if (xWindow && !xWindow.closed) xWindow.close();
          markShareBonusClaimed();
          notifySuccess("Opened share sheet with your PNL card.");
          return;
        } catch (shareErr) {
          if (shareErr?.name !== "AbortError" && shareErr?.name !== "NotAllowedError") {
            throw shareErr;
          }
        }
      }

      const copied = await copyImageToClipboard(blob).catch(() => false);
      if (!xWindow || xWindow.closed) {
        window.open(intentUrl, "_blank", "noopener,noreferrer");
      }
      markShareBonusClaimed();

      if (copied) {
        notifySuccess("Image copied — paste it into your post on X.");
      } else {
        notifySuccess("Opened X — use Download Image if you need the card file.");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error(err);
      notifyError("Could not share. Try downloading the image instead.");
    } finally {
      endAction();
    }
  }, [beginAction, canShare, endAction, generateBlob, markShareBonusClaimed, payload]);

  const showShareBonus = canShare && !shareBonusClaimed;

  const isSharing = activeAction === "share";
  const isDownloading = activeAction === "download";
  const isBusy = activeAction !== null;

  return {
    downloadImage,
    shareOnX,
    isSharing,
    isDownloading,
    isBusy,
    canShare,
    showShareBonus,
  };
}
